import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { useField } from 'informed';
import { getResizedImage } from 'shared-helpers/common';
import { breakpoints } from 'styles';
import { Button, PictureSelector } from 'components/common';
import { FormSection } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';
import { waitForImage } from 'lib/files';

export const CALENDAR_EVENT_IMAGE_ASPECT_RATIO = '960 / 200';

interface Props {
  id: string;
  field: string;
  onFileAdded: () => void;
  onFileRemoved: () => void;
  onUpload?: (value: any) => void;
  setUploader: (fileUploader: React.ReactElement<any>) => void;
}

const CalendarEventImage = (props: Props) => {
  const {
    messages,
    pageState: { calendarEvent },
    theme
  } = useReduxState();

  const { fieldApi, render, userProps } = useField({ ...props });

  const { onFileAdded, onFileRemoved, onUpload, setUploader } = userProps;

  const [image, setImage] = useState(calendarEvent.imageUrl || null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (calendarEvent.imageUrl) {
      fieldApi.setValue(calendarEvent.imageUrl);
      setImage(calendarEvent.imageUrl);
    }
  }, [calendarEvent.imageUrl]);

  const handleFileRemoved = () => {
    fieldApi.setValue(null);
    if (onFileRemoved) onFileRemoved();
    setPreview(null);
  };

  const handlePredefinedImage = (url: string) => {
    fieldApi.setValue(url);
    if (onFileRemoved) onFileRemoved();
    setPreview(url);
  };

  const handleRemoveImage = () => {
    setImage(null);
    handleFileRemoved();
  };

  const handleSuccess = async ({ url }) => {
    fieldApi.setValue(url);
    if (onUpload) onUpload(url);
    await waitForImage(getResizedImage({ size: 960, url }));
    setImage(url);
    setPreview(null); // clear the blob preview to avoid showing blurry image after upload
  };

  const handleThumbnail = ({ preview }) => {
    setPreview(preview);
    if (onFileAdded) onFileAdded();
  };

  return render(
    <FormSection title={messages.blocksImage}>
      <div className="calendar-image">
        <div className="actions">
          <div className="uploader">
            <FileUploader
              allowedFileTypes={['image/*']}
              getUploader={setUploader}
              icon="file"
              id={`image-uploader`}
              keyPrefix="uploads/images/original/"
              onFileRemoved={handleFileRemoved}
              onThumbnailGenerated={handleThumbnail}
              onUploadSuccess={handleSuccess}
              uuidAsFilename={true}
            />
          </div>

          <div className="or">{messages.commonOr.toUpperCase()}</div>

          <PictureSelector
            onSelect={handlePredefinedImage}
            selectedPicture={preview || image}
          />
        </div>

        {preview || image ? (
          <div className="preview">
            <div
              className="preview-image"
              style={{
                backgroundImage: `url(${getResizedImage({
                  size: 960,
                  url: preview || image
                })})`
              }}
            />
            <div className="preview-remove">
              <Button
                category="light"
                icon="trash-alt"
                onClick={handleRemoveImage}
                size="header"
                text={messages.commonRemove}
              />
            </div>
          </div>
        ) : null}

        <style jsx>{`
          .actions {
            display: flex;
            gap: 10px;
          }
          .or {
            color: ${theme.textColorLighter};
            font-size: 1.5rem;
            font-weight: 600;
            padding: 5px 0;
          }
          .options {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            width: 400px;
          }
          .option {
            display: flex;
            justify-content: center;
            width: 25%;
          }
          .options button.option-button {
            min-width: 0;
          }
          .option-content {
            height: 42px;
            position: relative;
          }
          .preview-image {
            aspect-ratio: ${CALENDAR_EVENT_IMAGE_ASPECT_RATIO};
            background-position: center;
            background-size: cover;
            border-radius: 10px;
            margin-top: 15px;
            max-width: 700px;
          }
          .preview-remove {
            margin-top: 10px;
          }
          @media (max-width: ${breakpoints.medium}px) {
            .options {
              width: 200px;
            }
            .option {
              width: 50%;
            }
          }
        `}</style>
      </div>
    </FormSection>
  );
};

export default CalendarEventImage;
